/**
 * §7.15 Activity Indicators — the surface that finally consumes the rate / ratio
 * metrics ingested in Phase 3 (UKHSA · CDC RESP-NET · Delphi NSSP · ECDC ERVISS)
 * that the situational map deliberately skips because they're INTENSITY signals,
 * not case incidence.
 *
 * Three panels, one per metric:
 *   - test-positivity      (% of specimens positive — UKHSA + ECDC ERVISS)
 *   - syndromic-rate       (% of ED visits for the syndrome — Delphi NSSP + UKHSA ILI)
 *   - hospitalisation-rate (per-100k admissions — CDC RESP-NET + UKHSA)
 *
 * Each rate carries its source + provenance chip + freshness. Stale rows are
 * demoted but visible (the same honesty rule the map uses). Built on the M2 kit
 * (Surface/Panel/KpiStrip/DataTable) so the scaffold stays consistent.
 */

import { useMemo } from 'react';
import { findPathogen } from '@biomax/plugin-pathogen-database';
import { listSignals, signalFreshness } from '@biomax/plugin-surveillance-feed';
import { Surface, Panel, KpiStrip, Kpi, DataTable, type DataColumn } from '../kit';
import { ProvChip, provKind } from '../ProvChip';

type Row = {
  id: string;
  pathogen: string;
  location: string;
  value: number;
  asOf: string;
  source: string;
  stale: boolean;
  raw: ReturnType<typeof listSignals>[number];
};

function rowsForMetric(metric: 'test-positivity' | 'syndromic-rate' | 'hospitalisation-rate'): Row[] {
  const now = Date.now();
  return listSignals()
    .filter(s => s.metric === metric)
    .map(s => {
      const stale = signalFreshness(s, now).stale;
      return {
        id: `${s.pathogenId}|${s.location.admin}|${s.source}|${s.asOf}`,
        pathogen: findPathogen(s.pathogenId)?.names.common ?? s.pathogenId,
        location: s.location.admin,
        value: Number(s.value) || 0,
        asOf: s.asOf,
        source: s.source,
        stale,
        raw: s,
      };
    })
    .sort((a, b) => Number(a.stale) - Number(b.stale) || b.value - a.value);
}

function fmtPct(v: number): string { return v.toFixed(v >= 10 ? 1 : 2) + '%'; }
function fmtRate(v: number): string { return v.toFixed(v >= 1 ? 1 : 2); }
function fmtDate(iso: string): string { return iso.slice(0, 10); }

function pathogenCol(): DataColumn<Row> { return { key: 'p', header: 'Pathogen', cell: r => r.pathogen, width: '22%' }; }
function locationCol(): DataColumn<Row> { return { key: 'l', header: 'Location', cell: r => <span style={{ color: 'var(--bm-text-muted)' }}>{r.location}</span>, width: '30%' }; }
function asOfCol(): DataColumn<Row> { return { key: 'd', header: 'As of', cell: r => <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-muted)' }}>{fmtDate(r.asOf)}</span>, width: '12%' }; }
function sourceCol(): DataColumn<Row> { return { key: 's', header: 'Source', cell: r => <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-subtle)' }}>{r.source}</span>, width: '15%' }; }
function provCol(): DataColumn<Row> { return { key: 'pv', header: 'State', cell: r => <ProvChip kind={provKind(r.raw, r.stale)} compact />, width: '8%' }; }

function valueCol(label: string, fmt: (v: number) => string, hot: (v: number) => boolean): DataColumn<Row> {
  return {
    key: 'v', header: label, num: true, width: '13%',
    cell: r => <span style={{ color: r.stale ? 'var(--bm-prov-stale)' : hot(r.value) ? 'var(--bm-threat-2)' : 'var(--bm-text)' }}>{fmt(r.value)}</span>,
  };
}

/** Inline trend sparkline + week-over-week arrow (CD review R3b). Reads
 *  `signal.history`; renders an em-dash when the source carries only a single
 *  observation. Width-fixed so the table column doesn't shimmy as rows expand. */
function TrendCell({ r }: { r: Row }) {
  const hist = r.raw.history;
  if (!hist || hist.length < 2) {
    // CD R3b note: a bare em-dash reads as "flat/zero". Explicit "no history"
    // pill prevents the operator misreading absence-of-series as steady-state.
    return (
      <span
        title="This source carries only a single observation per ingest — no trend series available yet."
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          font: '600 10px var(--bm-font-mono)',
          color: 'var(--bm-text-subtle)',
          padding: '1px 6px',
          border: '1px dashed var(--bm-border-strong)',
          borderRadius: 4,
        }}
      >
        <span aria-hidden>∅</span>
        <span>no history</span>
      </span>
    );
  }
  const values = hist.map(p => p.value);
  const w = 76, h = 22;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const X = (i: number): number => (i / (values.length - 1)) * w;
  const Y = (v: number): number => h - ((v - min) / range) * (h - 4) - 2;
  const pts = values.map((v, i) => `${X(i)},${Y(v)}`).join(' ');
  const last = values[values.length - 1]!;
  const prev = values[values.length - 2]!;
  const up = last > prev;
  const flat = last === prev;
  const col = r.stale ? 'var(--bm-prov-stale)' : up ? 'var(--bm-threat-2)' : 'var(--bm-prov-confirmed)';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} title={`${values.length}-week trend: ${values.map(v => v.toFixed(1)).join(' → ')}`}>
      <svg width={w} height={h} style={{ display: 'block' }} aria-hidden>
        <polyline points={pts} fill="none" stroke={col} strokeWidth="1.6" strokeLinejoin="round" />
        <circle cx={X(values.length - 1)} cy={Y(last)} r="2.4" fill={col} />
      </svg>
      <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: col }}>{flat ? '–' : up ? '▲' : '▼'}</span>
    </span>
  );
}
function trendCol(): DataColumn<Row> {
  return { key: 't', header: 'Trend', cell: r => <TrendCell r={r} />, width: '12%' };
}

export function ActivityIndicators() {
  const view = useMemo(() => {
    const positivity = rowsForMetric('test-positivity');
    const syndromic = rowsForMetric('syndromic-rate');
    const hospRate = rowsForMetric('hospitalisation-rate');
    const all = [...positivity, ...syndromic, ...hospRate];
    return { positivity, syndromic, hospRate, total: all.length, stale: all.filter(r => r.stale).length };
  }, []);

  return (
    <Surface
      section="§7.15"
      title="Activity Indicators · respiratory intensity (not case counts)"
      sub={<>Test positivity, syndromic ED-visit %, hospitalisation rates. Sources: UKHSA · CDC RESP-NET · Delphi NSSP · ECDC ERVISS. These say <em>how hot is it</em>, not <em>how many</em>; they are deliberately held off the situational map (no per-marker incidence semantics) but tracked here for trend + early-warning. EDU — NOT FOR OPERATIONAL DEPLOYMENT.</>}
    >
      <KpiStrip>
        <Kpi label="Test positivity rows"      value={view.positivity.length} sub="UKHSA + ECDC sentinel %" tone="accent" />
        <Kpi label="Syndromic ED-visit %"      value={view.syndromic.length}  sub="Delphi NSSP + UKHSA ILI" />
        <Kpi label="Hospitalisation rates"     value={view.hospRate.length}   sub="CDC RESP-NET + UKHSA /100k" />
        <Kpi label="Total indicators"          value={view.total}             sub="across all rate metrics" />
        <Kpi label="Stale"                     value={view.stale}             sub="older than 2× source cadence" tone={view.stale ? 'signal' : undefined} />
      </KpiStrip>

      <Panel title="Test positivity" meta={`${view.positivity.length} rows · highlighted ≥10%`}>
        <DataTable
          rows={view.positivity}
          rowKey={r => r.id}
          empty="No positivity indicators in the feed yet."
          scrollMaxHeight={360}
          columns={[
            pathogenCol(), locationCol(),
            valueCol('Positivity', fmtPct, v => v >= 10),
            trendCol(),
            asOfCol(), sourceCol(), provCol(),
          ]}
        />
      </Panel>

      <Panel title="Syndromic ED visits · % of all ED visits for the syndrome" meta={`${view.syndromic.length} rows · highlighted ≥1%`}>
        <DataTable
          rows={view.syndromic}
          rowKey={r => r.id}
          empty="No syndromic indicators in the feed yet."
          scrollMaxHeight={320}
          columns={[
            pathogenCol(), locationCol(),
            valueCol('ED visits %', fmtPct, v => v >= 1),
            trendCol(),
            asOfCol(), sourceCol(), provCol(),
          ]}
        />
      </Panel>

      <Panel title="Hospitalisation rates · admissions per 100,000 population" meta={`${view.hospRate.length} rows · highlighted ≥2/100k`}>
        <DataTable
          rows={view.hospRate}
          rowKey={r => r.id}
          empty="No hospitalisation-rate indicators in the feed yet."
          scrollMaxHeight={320}
          columns={[
            pathogenCol(), locationCol(),
            valueCol('Rate /100k', fmtRate, v => v >= 2),
            trendCol(),
            asOfCol(), sourceCol(), provCol(),
          ]}
        />
      </Panel>
    </Surface>
  );
}
